import React, { useRef } from 'react'
import type { Editor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import LinkIcon from '@material-ui/icons/Link'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft'
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter'
import FormatAlignRightIcon from '@material-ui/icons/FormatAlignRight'
import FormatAlignJustifyIcon from '@material-ui/icons/FormatAlignJustify'
import FormatColorTextIcon from '@material-ui/icons/FormatColorText'
import HighlightIcon from '@material-ui/icons/BorderColor'
import FormatClearIcon from '@material-ui/icons/FormatClear'
import { useSnackbar } from 'notistack'
import { InlineColorPicker } from '@paintscout/ui/components/ColorPicker'
import type { ToolbarConfig } from './Editor'

export interface EditorBubbleMenuProps {
  editor: Editor
  noDisplayClassName?: string
  toolbar?: ToolbarConfig
  blockColor?: boolean
}

const useStyles = makeStyles<Theme>((theme) => ({
  tippyMenu: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
    padding: 0,
    borderRadius: theme.borderRadius.sm,
    overflow: 'hidden',
    display: 'flex',
    maxWidth: 210,
    flexWrap: 'wrap',
    '& button': {
      background: 'none',
      border: 'none',
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.pxToRem(16),
      minWidth: theme.typography.pxToRem(30),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(0.5),
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.paintscout.palette.brand.Neutral.P50,
      cursor: 'pointer',
      '& svg': {
        fontSize: '1rem'
      },
      '&:hover': {
        color: theme.paintscout.palette.brand.Neutral.P40
      },
      '&.is-active': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P85
      },
      '&.bold': {
        fontWeight: theme.typography.fontWeightBold
      },
      '&.italic': {
        fontWeight: theme.typography.fontWeightBold,
        fontStyle: 'italic'
      },
      '&.underline': {
        fontWeight: theme.typography.fontWeightBold,
        textDecoration: 'underline'
      }
    }
  },
  linkInput: {
    width: '100%',
    borderRadius: theme.borderRadius.sm,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    borderTop: 0
  },
  pathFillIcon: {
    '& path': {
      fillOpacity: 1
    }
  }
}))

// Fixes disappearing menu on click w/ Safari Browser
const handleMouseDown = (ev: React.MouseEvent<HTMLButtonElement>) => {
  ev.preventDefault()
}

export function EditorBubbleMenu({
  editor,
  noDisplayClassName,
  toolbar: toolbarProp,
  blockColor
}: EditorBubbleMenuProps) {
  const classes = useStyles()
  const hrefInput = useRef(null)
  const { enqueueSnackbar } = useSnackbar()

  const defaultToolbar = {
    headings: true,
    bold: true,
    italic: true,
    underline: true,
    color: false,
    highlight: false,
    align: true,
    lists: false,
    link: true
  }
  const toolbar = { ...defaultToolbar, ...toolbarProp }

  const handleUnsetLink = () => {
    editor.chain().focus().unsetLink().run()
    enqueueSnackbar('Link removed', { variant: 'success' })
  }

  // Maintain current text align status to pass in on font change
  const alignmentOptions = {
    left: editor.isActive({ textAlign: 'left' }),
    center: editor.isActive({ textAlign: 'center' }),
    right: editor.isActive({ textAlign: 'right' }),
    justify: editor.isActive({ textAlign: 'justify' })
  }
  const currentTextAlign = Object.keys(alignmentOptions).filter((key) => alignmentOptions[key] === true)[0]

  return (
    <div className={noDisplayClassName}>
      <BubbleMenu editor={editor} tippyOptions={{ zIndex: 4 }}>
        <div id="bubble-menu" className={classes.tippyMenu}>
          {toolbar.bold && (
            <button
              type="button"
              onMouseDown={handleMouseDown}
              onClick={(ev) => {
                ev.preventDefault()
                editor.chain().focus().toggleBold().run()
              }}
              className={editor.isActive('bold') ? 'is-active bold' : 'bold'}
            >
              B
            </button>
          )}
          {toolbar.italic && (
            <button
              type="button"
              onMouseDown={handleMouseDown}
              onClick={(ev) => {
                ev.preventDefault()
                editor.chain().focus().toggleItalic().run()
              }}
              className={editor.isActive('italic') ? 'is-active italic' : 'italic'}
            >
              I
            </button>
          )}
          {toolbar.underline && (
            <button
              type="button"
              onMouseDown={handleMouseDown}
              onClick={(ev) => {
                ev.preventDefault()
                editor.chain().focus().toggleUnderline().run()
              }}
              className={editor.isActive('underline') ? 'is-active underline' : 'underline'}
            >
              U
            </button>
          )}
          {toolbar.headings && (
            <>
              <button
                type="button"
                onMouseDown={handleMouseDown}
                onClick={(ev) => {
                  ev.preventDefault()
                  editor.chain().focus().toggleHeading({ level: 1 }).setTextAlign(currentTextAlign).run()
                }}
                className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
              >
                H₁
              </button>
              <button
                type="button"
                onMouseDown={handleMouseDown}
                onClick={(ev) => {
                  ev.preventDefault()
                  editor.chain().focus().toggleHeading({ level: 2 }).setTextAlign(currentTextAlign).run()
                }}
                className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
              >
                H₂
              </button>
              <button
                type="button"
                onMouseDown={handleMouseDown}
                onClick={(ev) => {
                  ev.preventDefault()
                  editor.chain().focus().toggleHeading({ level: 3 }).setTextAlign(currentTextAlign).run()
                }}
                className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
              >
                H₃
              </button>
            </>
          )}
          {toolbar.color && !blockColor && (
            <InlineColorPicker
              swatchIcon={
                <button type="button" onMouseDown={handleMouseDown}>
                  <FormatColorTextIcon
                    style={{ color: editor.getAttributes('textStyle').color }}
                    className={classes.pathFillIcon}
                  />
                </button>
              }
              swatchOnly
              value={editor.getAttributes('textStyle').color}
              onChange={(color: string) => {
                editor.chain().focus().setColor(color).run()
              }}
              useClearOption
              defaultColor={'#000000'}
              onClear={() => editor.chain().focus().setColor('#000000').run()}
            />
          )}
          {toolbar.highlight && !blockColor && (
            <InlineColorPicker
              swatchIcon={
                <button type="button" onMouseDown={handleMouseDown}>
                  <HighlightIcon style={{ color: editor.getAttributes('highlight').color }} />
                </button>
              }
              swatchOnly
              value={editor.getAttributes('highlight').color ?? ''}
              onChange={(color: string) => {
                editor.chain().focus().setHighlight({ color }).run()
              }}
              useClearOption
              defaultColor={''}
              onClear={() => editor.chain().focus().unsetHighlight().run()}
              hideSelectionHighlight
            />
          )}
          {toolbar.align && (
            <>
              <button
                type="button"
                onMouseDown={handleMouseDown}
                onClick={(ev) => {
                  ev.preventDefault()
                  editor.chain().focus().setTextAlign('left').run()
                }}
                className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
              >
                <FormatAlignLeftIcon />
              </button>
              <button
                type="button"
                onMouseDown={handleMouseDown}
                onClick={(ev) => {
                  ev.preventDefault()
                  editor.chain().focus().setTextAlign('center').run()
                }}
                className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
              >
                <FormatAlignCenterIcon />
              </button>
              <button
                type="button"
                onMouseDown={handleMouseDown}
                onClick={(ev) => {
                  ev.preventDefault()
                  editor.chain().focus().setTextAlign('right').run()
                }}
                className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
              >
                <FormatAlignRightIcon />
              </button>
              <button
                type="button"
                onMouseDown={handleMouseDown}
                onClick={(ev) => {
                  ev.preventDefault()
                  editor.chain().focus().setTextAlign('justify').run()
                }}
                className={editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}
              >
                <FormatAlignJustifyIcon />
              </button>
            </>
          )}
          <button
            type="button"
            onMouseDown={handleMouseDown}
            onClick={(ev) => {
              ev.preventDefault()
              editor.chain().focus().unsetAllMarks().run()
            }}
          >
            <FormatClearIcon />
          </button>
          {toolbar.link && (
            <button
              type="button"
              onMouseDown={handleMouseDown}
              onClick={(ev) => {
                ev.preventDefault()
                editor.isActive('link')
                  ? handleUnsetLink()
                  : editor.chain().focus().toggleLink({ href: 'https://example.com' }).run()
              }}
              id="link-toggle-button"
              className={editor.isActive('link') ? 'is-active' : ''}
            >
              {editor.isActive('link') ? <LinkOffIcon /> : <LinkIcon />}
            </button>
          )}
          {editor.isActive('link') && (
            <input
              ref={hrefInput}
              className={classes.linkInput}
              type="text"
              onFocus={() => {
                if (!hrefInput?.current?.value) hrefInput.current.value = editor.getAttributes('link').href
              }}
              onBlur={(ev) => {
                const blurTarget = ev.relatedTarget as Element
                if (blurTarget?.id === 'link-toggle-button') {
                  return null
                }
                const href = hrefInput.current.value || 'https://example.com'
                editor.chain().focus().setLink({ href }).run()
                enqueueSnackbar(`Link to ${href} set`, { variant: 'success' })
              }}
              placeholder={editor.getAttributes('link').href}
            />
          )}
        </div>
      </BubbleMenu>
    </div>
  )
}
